import { useTellescopeQuery, type SupportedSession } from "@tellescope/tanstack-query";
import { decodeTranslationMapFromStorage } from "@tellescope/utilities";
import type { Form } from "@tellescope/types-models";
import { baseLanguageCode, resolveFormTranslationConfigurationId, type FormTranslationMap } from "../utils";

export interface UseFormTranslationsArgs {
  form: Pick<Form, "id" | "translationConfigurations"> | undefined;
  /** The active i18n language. English resolves to no map, since it is the source language. */
  language: string | undefined;
  /** Set on the public form link, where `configurations` cannot be read directly. */
  accessCode?: string;
  /** Omitted on surfaces with no session, which render the authored English. */
  session?: SupportedSession | null;
}

/**
 * Loads a form's configured translations for the active language.
 *
 * Transport depends on how the form was opened. An authenticated session reads the `configurations`
 * record the form points at and decodes it. The public form link has no such access, so it goes
 * through `public_form_details`, which returns the map already decoded — and returns it undefined
 * when the server's `allowPublicURL`/`publicRead` gate declines.
 *
 * Nothing is fetched for English, or for a language this form has no translation for; both resolve to
 * `undefined`, which renders the authored English. A failed fetch resolves the same way rather than
 * throwing, so a translation problem never blocks filling out the form.
 */
export const useFormTranslations = ({
  form,
  language,
  accessCode,
  session,
}: UseFormTranslationsArgs): FormTranslationMap | undefined => {
  const configurationId = resolveFormTranslationConfigurationId(form, language);
  const languageCode = baseLanguageCode(language);
  const formId = form?.id;

  const isTranslated = !!session && !!formId && !!configurationId;

  // The map is immutable for a given configuration, so it never needs refetching, and a failure is
  // not worth retrying — the form renders in English either way.
  const configurationQuery = useTellescopeQuery<"configurations", "getOne", Error, FormTranslationMap>({
    model: "configurations",
    method: "getOne",
    request: configurationId ?? "",
    enabled: isTranslated && !accessCode,
    select: ({ value }) => decodeTranslationMapFromStorage(value),
    staleTime: Infinity,
    retry: false,
    session: session ?? undefined,
  });

  const publicQuery = useTellescopeQuery<"forms", "public_form_details", Error, FormTranslationMap>({
    model: "forms",
    method: "public_form_details",
    request: { formId: formId ?? "", language: languageCode },
    enabled: isTranslated && !!accessCode,
    select: ({ translations }) => translations ?? {},
    staleTime: Infinity,
    retry: false,
    session: session ?? undefined,
  });

  return accessCode ? publicQuery.data : configurationQuery.data;
};
